import { z } from 'zod';
import {
  amountSchema,
  partySchema,
  paymentMethodSchema,
  paymentStatusCommonSchema,
  paymentTermsSchema,
} from '../../common';
import { dateSchema, objectIdSchema, priceSchema } from '../../validation';
import {
  orderInvoiceSchema,
  orderPaymentTransactionSchema,
  orderPricingStrategySchema,
} from './order.payment.model';

/**
 * audit snapshot of the `Route` entry that priced this contract.
 *
 * Stored as a struct so the snapshot survives later edits to the source Route
 * (the `price` here is "frozen" at create time, even if the underlying Route
 * price changes from 1500 to 1600 next month).
 *
 * `agreementId` is optional and present iff the resolved Route had
 * `Route.agreementId` set (= directed Route bound to an Agreement). Its
 * presence is the audit link back to the contractual wrapper without us
 * having to re-query the Route doc to discover the agreement.
 *
 * Reused by both:
 *   - `orderContractElementSchema.route` (the snapshot on the realized
 *     contract leg).
 *   - `offerTargetSchema.pricing.route` (the snapshot on the offer row before
 *     it is accepted).
 */
export const contractRouteRefSchema = z.object({
  /** the source Route - see route.model.ts */
  routeId: objectIdSchema,
  /**
   * snapshot of the Route's neutral "price BEFORE fees/tax" at creation time
   * (= `Amount.itemsOriginalSubtotal` in `amount.schema.ts`).
   *
   * Point-in-time - changing the Route later does NOT mutate this.
   */
  price: priceSchema,
  /**
   * audit link to the contractual wrapper, present iff the resolved Route had
   * `agreementId` set. Lets callers resolve the Agreement (terms, parties,
   * dates) for invoicing / display without an extra round trip to the Route.
   */
  agreementId: objectIdSchema.optional(),
});
export type ContractRouteRef = z.infer<typeof contractRouteRefSchema>;

/**
 * Final commercial state of a contract leg's load price — whether the
 * **current** `amount.itemsOriginalSubtotal` honors the frozen
 * {@link contractRouteRefSchema} quote on this leg.
 *
 * Mutable on create and whenever the load price is edited. Distinct from
 * {@link orderPricingStrategySchema} which records **origin at birth**
 * only (audit metadata, never updated post-create).
 *
 *   - `route_quoted` — `leg.route` exists and load price ≈ `leg.route.price`.
 *   - `manual`       — everything else (no snapshot, one-time create,
 *                      unresolved fill-in, offer counter, admin override,
 *                      or drift from the frozen quote).
 */
export const orderContractPriceBasisSchema = z.enum(['route_quoted', 'manual']);
export type OrderContractPriceBasis = z.infer<typeof orderContractPriceBasisSchema>;

/**
 * commercial status of a single embedded contract element.
 *
 * Per-leg state - lives at `Order.contracts[i].status`. Independent from
 * `Order.status.order` (which is the physical state of the load).
 *
 * Most contracts are created already-accepted (an Offer was accepted, which
 * spawns the contract via OrderService.pushAcceptedContract). The `offered`
 * and `counter_proposed` states exist for niche cases where an admin/system
 * inserts a contract that has not yet been confirmed.
 */
export const orderContractStatusSchema = z.enum([
  'offered',
  'accepted',
  'declined',
  'counter_proposed',
  'cancelled',
  'completed',
]);
export type OrderContractStatus = z.infer<typeof orderContractStatusSchema>;

/**
 * Tag for a commercial state-transition event scoped to a single
 * contract leg. Phase 1 folds per-leg events into the order-wide
 * {@link orderTimelineEventSchema} (with `contractId` set) instead of
 * a nested `OrderContractElement.events[]` array, so this enum now
 * exists only as the source of truth for the `contract_*` variants
 * exposed via {@link orderEventTypesSchema}:
 *
 *   `contract_created`, `contract_accepted`, `contract_declined`,
 *   `contract_countered`, `contract_cancelled`, `contract_completed`.
 *
 * Kept exported (vs. inlined into the timeline enum) so the union is
 * defined once and referenced from both sides.
 */
export const orderContractEventTypeSchema = z.enum([
  'created',
  'accepted',
  'declined',
  'countered',
  'cancelled',
  'completed',
]);
export type OrderContractEventType = z.infer<typeof orderContractEventTypeSchema>;

/**
 * Per-leg payment block.
 *
 * Phase 7 (relations-v2): each contract leg is its own commercial unit
 * with its own payment cadence (different agreements can mean different
 * `terms` for different hops in the same chain). The buyer-side and
 * seller-side **invoice effects** for this leg live in
 * {@link orderContractElementSchema.invoices} alongside this block.
 */
export const orderContractPaymentSchema = z.object({
  /** payment status for this leg (`paid` / `not_paid` / `partially_paid`). */
  status: paymentStatusCommonSchema,
  /**
   * payment terms agreed for this leg. Defaults to immediate at create
   * time, replaced by the agreement / route terms when the resolver
   * has them.
   */
  terms: paymentTermsSchema,
  /**
   * how the buyer settles this leg.
   *
   * Defaults to `bank_transfer` since that's the canonical B2B mode in
   * the network. The user can flip to `cash` per-order in the price
   * drawer (e.g. cash-on-delivery for an individual customer or
   * walk-in load). Org-level defaults (e.g. "this Org always pays
   * cash") populate this at order creation; per-order edits override.
   *
   * `.default()` keeps existing docs valid - any contract leg without
   * an explicit method is read as `bank_transfer` by zod parsing,
   * which matches the historical assumption.
   */
  method: paymentMethodSchema.default('bank_transfer'),
});
export type OrderContractPayment = z.infer<typeof orderContractPaymentSchema>;

/**
 * a single commercial leg embedded inside `Order.contracts[]`.
 *
 * A chain `C -> B -> X -> B2 -> Y` produces 4 array elements. Chain order is
 * recovered dynamically from buyer/seller matching:
 * `downstream.buyer._id === upstream.seller._id`. Within a single Order each
 * hop is unique, so the (buyer, seller) pair unambiguously identifies position.
 * Helper: `OrderService.buildContractChain(order.contracts)` returns the
 * ordered list.
 *
 * No `parentContractId` (chain is derived). No per-contract `ref` (the Order's
 * `ref.main` is the shared identifier).
 *
 * Phase 7 (relations-v2): the leg is now the **single source of truth for
 * commercial state**. The deprecated `Order.payment.{beneficiary,carrier,
 * commission}` block has been removed; per-leg amounts live on
 * {@link amountSchema} (`paid` and `remaining` already there), and per-leg
 * payment cadence + ledger live on {@link orderContractPaymentSchema} and the
 * {@link invoices} / {@link transactions} arrays here. A contract leg can
 * effect 1–N invoice docs (e.g. when the agreement splits billing into
 * installments or when buyer-side and seller-side accounting need separate
 * invoices from the same leg).
 *
 * Phase 1: per-leg `events[]` is removed. All commercial state transitions
 * (`contract_created`, `contract_accepted`, `contract_declined`,
 * `contract_countered`, `contract_cancelled`, `contract_completed`) are
 * written as entries on `Order.timeline` with `contractId` set, so reading
 * the audit trail of an Order is a single array scan. See
 * `order.timeline.model.ts` and `docs/relations-v2/06-scenarios.md`.
 *
 * **ACL via centralized projection.** Order visibility is gated upstream by
 * `Order.tenants[]` (accepted relationships only). Within a visible Order,
 * `OrderService.projectOrderForViewer` filters `contracts[]` to legs where the
 * viewer is `buyer._id` or `seller._id`.
 */
export const orderContractElementSchema = z.object({
  /**
   * identity of this contract within the Order. NOT a separate Mongoose doc -
   * just an embedded subdoc id that the chain helpers and Offer.acceptedTarget
   * point at.
   */
  contractId: objectIdSchema,

  /**
   * the party paying for this hop (the buyer side of this bilateral deal).
   * Phase 1: party-shaped — `roles[]` + `ids[]` so `TenantsBuilder` can emit
   * `client_id` / `org_id` / `profile_id` tenants directly from contract legs.
   */
  buyer: partySchema,
  /**
   * the party being paid for this hop (the seller side of this bilateral deal).
   * Phase 1: party-shaped — see `buyer`.
   */
  seller: partySchema,

  /**
   * which pricing model applied at the time of contract creation. See
   * `order.payment.model.ts`.
   */
  pricingStrategy: orderPricingStrategySchema,
  /**
   * whether the **current** load price honors the frozen route quote on
   * this leg. See {@link orderContractPriceBasisSchema}. Updated on
   * create and when `itemsOriginalSubtotal` changes via the price-edit
   * drawer; `pricingStrategy` above stays immutable.
   */
  priceBasis: orderContractPriceBasisSchema.default('manual'),
  /**
   * the actual realized price for this hop. Rich amount block: total /
   * subtotal / tax / fees / discount / paid / remaining / currency. See
   * `amount.schema.ts`.
   *
   * Broker margin is computed at read time, not stored:
   * `sellContract.amount.realizedSubtotal - buyContract.amount.realizedSubtotal`.
   */
  amount: amountSchema,

  // ──────────── Source references ────────────
  // Which field(s) are populated tells you where the pricing came from.
  // No separate `pricingSource` enum is stored.

  /**
   * present when the contract was priced from an entry in the `Route`
   * collection. The snapshot's `agreementId` (when set) tells you that the
   * resolved Route was contract-bound at resolution time.
   *
   *   - `route` set, no `offerId`        -> walk-in pricing off a Route the
   *     creator's app surface resolved (self-issued order).
   *   - `route` set with `offerId`       -> the originating Offer's accepted
   *     target row was priced via Route; the snapshot is copied verbatim from
   *     `acceptedTarget.pricing.route`.
   *   - `route` absent                   -> price was manually proposed /
   *     countered (admin override or buyer counter). The original Route may
   *     still have been used as the audit context on the parent Offer.
   */
  route: contractRouteRefSchema.optional(),
  /**
   * the Offer this contract was created from (the chain-spawning trail).
   * When set, OrderService can look up the parent Offer and reconstruct the
   * full broadcast context (siblings, counter history, etc.) via the Offer
   * collection.
   */
  offerId: objectIdSchema.optional(),
  /**
   * the specific marketplace bid that was accepted, when the contract came
   * from a `public_load_board` Offer with open bidding. Used together with
   * `offerId`. (Future - wired in once `Offer.bids[]` is enabled.)
   */
  bidId: objectIdSchema.optional(),

  /** per-leg commercial state. See {@link orderContractStatusSchema}. */
  status: orderContractStatusSchema,

  /**
   * Per-leg payment cadence. Set at create time from the resolver
   * (route/agreement terms or the `immediate` default). Mutated by the
   * payment capture flow as money flows through this leg.
   */
  payment: orderContractPaymentSchema,

  /**
   * Invoice docs effected by this contract leg. A leg can effect 1–N
   * invoices depending on agreement terms (e.g. installment splits) and
   * on whether the buyer-side and seller-side accounting need separate
   * invoice records.
   *
   * Each entry is a thin denormalized snapshot pointing at the canonical
   * `Invoice` doc; live billing state continues to live on the Invoice
   * collection. This array is the index that lets us answer "which
   * invoices does this leg effect?" without a secondary collection
   * scan.
   */
  invoices: z.array(orderInvoiceSchema).default([]),

  /**
   * Transactions captured against this contract leg's invoices. Mirrors
   * what was previously written to `Order.payment.{beneficiary,carrier}.
   * payment.transactions` — but per-leg, so a chain `C → B → X` keeps
   * the buyer-side capture (`contracts[0].transactions`) and the
   * carrier-side capture (`contracts[2].transactions`) cleanly
   * separated.
   */
  transactions: z.array(orderPaymentTransactionSchema).default([]),

  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export type OrderContractElement = z.infer<typeof orderContractElementSchema>;

/**
 * convenience accessor: `Order.contracts[]` is just an array of
 * {@link orderContractElementSchema}.
 */
export const orderContractsSchema = z.array(orderContractElementSchema).default([]);
export type OrderContracts = z.infer<typeof orderContractsSchema>;
